/**
 * UwRundeEntscheidDialog - the broker records the customer's answer to an open round on the customer's
 * behalf (broker_erfasst; P2 Slice 14 D3). response_channel is email / telefon / persoenlich (portal is
 * the customer's own route). Abgelehnt needs a Grund.
 */

import { useEffect, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import type { UnderwritingResponse, UwRundeEntscheidBody } from '@brokly/shared';

const inputCls = 'w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ERROR_TEXT: Record<string, string> = {
  grund_required: 'Bitte einen Grund angeben',
  runde_entschieden: 'Diese Freigabe wurde bereits beantwortet',
  antrag_status_invalid: 'Der Antrag ist nicht mehr in diesem Status',
  response_channel_invalid: 'Der Rueckmeldekanal ist ungueltig',
};

export function UwRundeEntscheidDialog({
  open,
  onClose,
  facts,
  onSubmit,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  facts: UnderwritingResponse;
  onSubmit: (body: UwRundeEntscheidBody) => void;
  saving: boolean;
  error: string | null;
}) {
  const [entscheid, setEntscheid] = useState<'akzeptiert' | 'abgelehnt'>('akzeptiert');
  const [channel, setChannel] = useState<'email' | 'telefon' | 'persoenlich'>('telefon');
  const [name, setName] = useState(facts.kunde_name);
  const [grund, setGrund] = useState('');

  useEffect(() => {
    if (open) {
      setEntscheid('akzeptiert');
      setChannel('telefon');
      setName(facts.kunde_name);
      setGrund('');
    }
  }, [open, facts.kunde_name]);

  const grundOk = entscheid === 'akzeptiert' || grund.trim().length >= 3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kundenentscheid erfassen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Zurueck</Button>
          <Button
            onClick={() => onSubmit({ entscheid, response_channel: channel, entscheider_name: name.trim(), grund: grund.trim() || null })}
            disabled={saving || name.trim().length < 1 || !grundOk}
            data-testid="uw-runde-entscheid-submit"
          >
            Kundenentscheid erfassen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="uw-runde-entscheid-dialog">
        <fieldset className="text-sm">
          <legend className="text-muted-foreground">Entscheid</legend>
          <label className="mr-4"><input type="radio" name="uw-runde-entscheid" checked={entscheid === 'akzeptiert'} onChange={() => setEntscheid('akzeptiert')} data-testid="uw-runde-akzeptiert" /> Akzeptiert</label>
          <label><input type="radio" name="uw-runde-entscheid" checked={entscheid === 'abgelehnt'} onChange={() => setEntscheid('abgelehnt')} data-testid="uw-runde-abgelehnt" /> Abgelehnt</label>
        </fieldset>
        <label className="text-sm">
          Rueckmeldekanal
          <select value={channel} onChange={(e) => setChannel(e.target.value as 'email' | 'telefon' | 'persoenlich')} className={inputCls} data-testid="uw-runde-channel">
            <option value="email">E-Mail</option>
            <option value="telefon">Telefon</option>
            <option value="persoenlich">Persoenlich</option>
          </select>
        </label>
        <label className="text-sm">
          Entscheider
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="uw-runde-name" />
        </label>
        {entscheid === 'abgelehnt' && (
          <label className="text-sm">
            Grund
            <textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} className={inputCls} data-testid="uw-runde-grund" />
          </label>
        )}
        {error && <p role="alert" className="text-sm text-status-danger" data-testid="uw-runde-entscheid-error">{ERROR_TEXT[error] ?? error}</p>}
      </div>
    </Dialog>
  );
}
